import { CardsProps } from "@amzn/awsui-components-react";
import { CatalogItem } from "./itseCatalogSchema";

/**
 * Represents the different sections that can appear in a standard order card component
 *
 * @enum {string}
 * @readonly
 * @property {string} SKU - Represents the order sku
 * @property {string} TAXONOMY - Represents the order taxonomy
 * @property {string} CUSTOM_BUTTON - Representing a custom button
 */
export enum StandardOrderCardSections {
    SKU = "SKU",
    TAXONOMY = "taxonomy",
    CUSTOM_BUTTON = "remove",
}

/**
 * Represents the different sections that can appear in a non standard order card component
 *
 * @enum {string}
 * @readonly
 * @property {string} MANUFACTURER - Represents the order sku
 * @property {string} SKU - Represents the order sku
 * @property {string} LINK - Represents the order product link
 * @property {string} DESCRIPTION - Represents the order description
 * @property {string} QUANTITY - Represents the order quantity
 * @property {string} CUSTOM_BUTTON - Representing a custom button
 */
export enum NonstandardOrderCardSections {
    MANUFACTURER = "manufacturer",
    SKU = "SKU",
    LINK = "link",
    DESCRIPTION = "description",
    QUANTITY = "quantity",
    CUSTOM_BUTTON = "remove",
}

/**
 * Represent the props for the useStandardOrder hook
 * @interface
 * @property {Map<CatalogRequestResponse, number>} standardOrder - The standard orders being managed by the hook
 * @property {React.Dispatch<React.SetStateAction<Map<CatalogRequestResponse, number>>>} setStandardOrder - The react setter
 * function for the standardOrder property
 */
export interface useStandardOrderProps {
    standardOrder: Map<CatalogItem, number>;
    setStandardOrder: React.Dispatch<React.SetStateAction<Map<CatalogItem, number>>>;
}

/**
 * Represent the output values for the useStandardOrder hook
 * @interface
 * @property {CatalogRequestResponse[]} catalog - Represents the current catalog of standard orders
 * @property {string} trackBy - A unique identifier for a standard order
 * @property {(item: CatalogRequestResponse) => number} getQuantity - Function that returns the quantity of a catalog item
 * @property {(item: CatalogRequestResponse, value: number) => void} updateQuantity - Function that updates the quantity of a catalog item
 * @property {(item: CatalogRequestResponse) => void} removeItem - Function to remove an item from the catalog
 * @property {() => boolean} canRemoveItems - Function that returns if items can be removed
 * @property {(quantity: number) => boolean} isValidQuantity - Function that determines if the current quantity is valid
 */
export interface useStandardOrderOutput {
    catalog: CatalogItem[];
    trackBy: string;
    getQuantity: (item: CatalogItem) => number;
    updateQuantity: (item: CatalogItem, value: number) => void;
    removeItem: (item: CatalogItem) => void;
    canRemoveItems: () => boolean;
    isValidQuantity: (quantity: number) => boolean;
}

/**
 * Represent the output values for the getUseStandardOrder hook
 *
 * Uses the useStandardOrderProps interface
 *
 * @interface
 */
export interface getUseStandardOrderOutput extends useStandardOrderProps {}

/**
 * Represent the props for the LocalITOrderConfirmationContent component
 * @interface
 * @property {boolean} isOrderStandard - Represents if the order is standard or non standard
 */
export interface LocalITOrderConfirmationContentProps extends useStandardOrderProps, useNonstandardOrderProps {
    isOrderStandard: boolean;
    getHasError: (id: string) => string | undefined;
}

/**
 * The unique identifier for a non standard order.
 * @constant {string}
 */
export const nonStandardHardwareUniqueIdentifier: string = "id";

/**
 * Represent the format of a non standard hardware (Only the minimum required elements)
 *
 * @interface
 * @property {string} manufacturer - The manufacturer of the hardware
 * @property {string} sku - The sku of the hardware
 * @property {number} quantity - The quantity requested of the hardware
 */
export interface RequiredNonStandardHardwareParams {
    sku: string;
    manufacturer: string;
    quantity: number;
}

/**
 * Type representing a mapping of required non-standard hardware parameters to their string identifiers
 * @type {Object}
 */
export type NonStandardRequiredParamsMap = {
    [key in keyof RequiredNonStandardHardwareParams]: string;
};

/**
 * Constant containing the string identifiers for required non-standard hardware parameters
 * @constant {NonStandardRequiredParamsMap}
 * @property {string} sku - Identifier for the SKU parameter
 * @property {string} manufacturer - Identifier for the manufacturer parameter
 * @property {string} quantity - Identifier for the quantity parameter
 */
export const NonStandardIds: NonStandardRequiredParamsMap = {
    sku: "sku",
    manufacturer: "manufacturer",
    quantity: "quantity",
};

/**
 * Represent the format of a non standard hardware
 *
 * @interface
 * @property {string} id - A unique id for the hardware (only used in UI)
 * @property {string} link - The product link
 * @property {string} description - The description of the hardware
 */
export interface NonStandardHardwareFormat extends RequiredNonStandardHardwareParams {
    id: string;
    link: string;
    description: string;
}

/**
 * Represent the props for the useNonstandardOrder hook
 * @interface
 * @property {NonStandardHardwareFormat[]} nonStandardOrder - A list of non standard orders to use
 * @property {React.Dispatch<React.SetStateAction<NonStandardHardwareFormat[]>>} setNonStandardOrder - The react setter
 * function for the standardOrder property
 */
export interface useNonstandardOrderProps {
    nonStandardOrder: NonStandardHardwareFormat[];
    setNonStandardOrder: React.Dispatch<React.SetStateAction<NonStandardHardwareFormat[]>>;
}

/**
 * Represent the output values for the useNonstandardOrder hook
 * @interface
 * @property {NonStandardHardwareFormat[]} catalog - Represents the current catalog of non standard order
 * @property {string} trackBy - A unique identifier for a non standard order
 * @property {(id: string, updates: Partial<NonStandardHardwareFormat>) => void} updateNonStandardHardware - function
 * that updates a unique parameter of a non standard order
 * @property {(id: string) => void} removeItem - A function to remove a non standard order from the catalog
 * @property {(id: string) => string} getItemName - A function to retrieve the name of a non standard order
 * @property {() => void} addNonStandardHardware - A function to add a non standard order to the catalog
 * @property {boolean} canAddMore - A flag determining if more non standard orders can be added
 * @property {() => boolean} canRemoveItems - Function that returns if items can be removed
 * @property {(quantity: number) => number} enforceMinimumQuantity - Function that enforces the minimum quantity allowed
 * @property {(quantity: number) => boolean} isValidQuantity - Function that determines if the current quantity is valid
 */
export interface useNonStandardOrderOutput {
    catalog: NonStandardHardwareFormat[];
    trackBy: string;
    updateNonStandardHardware: (id: string, updates: Partial<NonStandardHardwareFormat>) => void;
    removeItem: (id: string) => void;
    getItemName: (id: string) => string;
    addNonStandardHardware: () => void;
    canAddMore: boolean;
    canRemoveItems: () => boolean;
    enforceMinimumQuantity: (quantity: number) => number;
    isValidQuantity: (quantity: number) => boolean;
}

/**
 * Represent the output values for the getUseNonStandardOrder hook
 *
 * Uses the useNonstandardOrderProps interface
 *
 * @interface
 */
export interface getUseNonStandardOrderOutput extends useNonstandardOrderProps {}

/**
 * Represent the props for the NonStandardHardwareOrderConfirmation component
 *
 * Uses the useNonstandardOrderProps interface
 *
 * @interface
 * @param {CardsProps.CardsLayout[]} cardsLayout - The cards layout for the non standard orders
 * @param {(id: string) => string | undefined} getHasError - retrieves if an id has an error
 */
export interface NonStandardHardwareOrderConfirmationProps extends useNonstandardOrderProps {
    cardsLayout: CardsProps.CardsLayout[];
    getHasError: (id: string) => string | undefined;
}

/**
 * Represent the props for the StandardHardwareOrderConfirmation component
 *
 * Uses the useStandardOrderProps interface
 *
 * @interface
 * @param {CardsProps.CardsLayout[]} cardsLayout - The cards layout for the non standard orders
 * @param {(id: string) => string | undefined} getHasError - retrieves if an id has an error
 */
export interface StandardHardwareOrderConfirmationProps extends useStandardOrderProps {
    cardsLayout: CardsProps.CardsLayout[];
    getHasError: (id: string) => string | undefined;
}
